import {
  MRT_GlobalFilterTextField,
  MaterialReactTable,
  type MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { Box, Button, lighten, Stack } from '@mui/material';
import { KeyboardArrowDown, RemoveRedEye } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CurrencyFormat from '@/utils/CurrencyFormat';

type StudentData = {
  student: {
    id: string;
    nome: string;
  };
  total_payments: number;
  total_payments_expiring: number;
  total_payments_expired: number;
  medium_days_expire: number;
  total_paid_time: number;
  total_paid_after: number;
  financial_score: number;
  total_discount: number;
  bolsista: boolean; 
};

const columns: Array<MRT_ColumnDef<StudentData>> = [
  {
    accessorKey: 'student.nome',
    header: 'Aluno',
    sortDescFirst: true,
    enableMultiSort: true,
    Cell: ({ renderedCellValue }) => (
      <div style={{ marginLeft: '5px' }}>{renderedCellValue}</div>
    ),
  },

  {
    accessorKey: 'total_discount',
    header: 'Desconto %',
    Cell: ({ renderedCellValue }) => (
      // <div style={{ marginLeft: '20px' }}>{}</div>
      <Box
      sx={{
        height: '60px',
        width: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
     {renderedCellValue} %
    </Box>
    ),
  },
  {
    accessorKey: 'geral_score',
    header: 'Score médio',
    Cell: ({ renderedCellValue }) => (
      <Box
        sx={{
          backgroundColor: '#CEFACE',
          height: '60px',
          width: '60%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
       {renderedCellValue}
      </Box>
    ),
  },
  {
    accessorKey: 'financial_score',
    header: 'Score financeiro',
    Cell: ({ renderedCellValue, row }) => {
      const isBolsista = row.original.bolsista;
      return (
        <Box
          sx={{
            marginLeft: '40px',
            color: isBolsista ? '#E8BE32' : 'inherit',
          }}
        >
          {isBolsista ? 'Bolsista' : renderedCellValue}
        </Box>
      );
    },
  },
  {
    accessorKey: 'pedagogical_score',
    header: 'Score pedagógico',
    Cell: ({ renderedCellValue }) => (
      <div style={{ marginLeft: '40px' }}>{renderedCellValue}</div>
    ),
  },
  {
    accessorKey: 'frequency_score',
    header: 'Score frequência',
    Cell: ({ renderedCellValue }) => (
      <Box
        sx={{
          // backgroundColor: 'rgba(252, 228, 212, 1)',
          height: '60px',
          width: '50%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
       {renderedCellValue}
      </Box>
    ),
  },
  {
    accessorKey: 'is_active',
    header: 'Status',
    size: 40,
    minSize: 40,
    maxSize: 100,
    Cell: ({ renderedCellValue }) => (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          background: 'rgba(239, 239, 239, 1)',
          borderRadius: '32px',
          padding: '4px 8px',
          textAlign:'center',
          justifyContent: 'center',
          width: '100px',
        }}
      >
        <div
          style={{
            background: renderedCellValue ? 'rgba(15, 219, 98, 1)' : 'rgba(255, 0, 0, 1)', // Verde para ativo, vermelho para inativo
            borderRadius: '50%',
            width: '10px',
            height: '10px',
            marginRight: '5px',
            textAlign: 'center'
          }}
        ></div>
        <div>

        {renderedCellValue ? 'Ativo' : 'Inativo'}
        </div>

      </div>
    ),
  },
  
];

type TableHeaderCustomProps = {
  data: StudentData[];
  toBack: string;
};

const TableHeaderCustomTermometroGeral = ({ data, toBack }: TableHeaderCustomProps) => {
  const navigate = useNavigate();
  const tableBasic = useMaterialReactTable({
    columns,
    data,
    enableGlobalFilter: true,
    positionActionsColumn: 'last',
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnFilters: false,
    enableHiding: false,
    enableExpandAll: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enableRowActions: true,
    enableRowSelection: false,
    enableBottomToolbar: false,
    localization: {
      search: 'Pesquisar por nome do aluno',
      actions: 'Ações', 
    },
    initialState: {
      showGlobalFilter: true,
    },
    paginationDisplayMode: 'default',
    enablePagination: false,
    muiSearchTextFieldProps: {
      variant: 'outlined',
      size: 'small',
      fullWidth: true,                
      placeholder: 'Pesquisar por nome…',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30],
      shape: 'rounded',
      variant: 'outlined',
    },
    renderRowActions: ({ row }) => {
      const handleClick = () => {
        const id = row.original.student.id;
        const name = row.original.student.nome;
        navigate(toBack, { state: { id, name } });
      };

      return (
        <Stack alignItems={'center'}>
          <RemoveRedEye onClick={handleClick} />
        </Stack>
      );
    },
    muiTableHeadCellProps: () => ({
      sx: {
        backgroundColor: 'rgb(247, 247, 247)',
        color: 'black',
        fontWeight: 400,
        fontSize: '12px',
      },
    }),
    muiTableBodyCellProps: () => ({
      sx: {
        backgroundColor: '#fff',
        color: 'rgba(128, 128, 128, 1)',
        fontWeight: 400,
        fontSize: '12px',
        paddingX: '12px',
        padding: '0',
      },
    }),
    renderTopToolbar: ({ table }) => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={1}
      >
        {/* LEFT‑aligned, full width filter */}
        <Box flexGrow={6} mr={2}>
          <MRT_GlobalFilterTextField
            table={table}
            //@ts-ignore
            textFieldProps={{
              placeholder: 'Pesquisar por nome do aluno…',
              variant: 'outlined',
              size: 'small',
              fullWidth: true,
            }}
          />
        </Box>

        {/* if you have any right‑side buttons, put them here */}
        {/* <Button onClick={…}>My Action</Button> */}
      </Box>
    )
  });

  return (
    <>
      <MaterialReactTable table={tableBasic} />
    </>
  );
};

export default TableHeaderCustomTermometroGeral;
